import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FindLoad, FindSuccessItem, Images, renderDate, renderStaffName, SaveParams, useModal } from 'utils';
import { DateTypeSelect, MinLoader, TextRow, ToolInfo, CustomDelete, SimpleTooltip } from 'components';
import { formTabEnums, useFormBuilder } from '../formDetail';
import { goalActions, httpRequestsOnSuccessActions } from 'store';
import { DataFields } from './dataFields';

const GRAPH_DATA_ACTIONS = {
    GOAL: 'GET_GOAL_CLINICAL_DATA',
    TARGET: 'GET_GOAL_TARGET_CLINICAL_DATA',
    DEFAULT_VALUES: 'GET_GOAL_CLINICAL_DEFAULT_VALUES',
};

/**
 * `canSaveDraft`: a new report or a draft — shows **Save Draft** next to **Submit**.
 * A finalized report has one **Save**. `onSave(asDraft)`.
 */
export const ProgressReportBuilderHeader = ({ onSave, uploading, canSaveDraft }) => {
    const { id, clientId, reportId } = useParams();
    const { formList, setActiveSection, setSelectedIndex, isDirty, setIsDirty, initialFormListStr } = useFormBuilder();
    const ACTION_TYPE = reportId ? 'EDIT_PROGRESS_REPORT' : 'CREATE_PROGRESS_REPORT';
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const activeTab = info?.activeTab || formTabEnums.CREATOR;
    const loader = FindLoad(ACTION_TYPE);
    // Save spans two phases: phase-1 uploads pending signatures/images to the
    // file service (signaled via `uploading`), phase-2 is the create/edit request
    // (tracked by `loader`). Spin across both so the button never looks idle mid-save.
    const isSaving = !!loader?.length || !!uploading;
    // Which button started the save, so only that one spins.
    const [savingDraft, setSavingDraft] = useState(false);
    const [dateEditor, setDateEditor] = useState({});
    const dispatch = useDispatch();
    const allSelectedGoals = (formList?.sections || []).flatMap((s) => s?.selectedGoals || []);
    const success = FindSuccessItem(ACTION_TYPE);
    const { open, close } = useModal();
    const progressReportById = useSelector((state) => state.progressReports.progressReportById);
    const graphLoads = [
        ...(FindLoad(GRAPH_DATA_ACTIONS.GOAL) || []),
        ...(FindLoad(GRAPH_DATA_ACTIONS.TARGET) || []),
        ...(FindLoad(GRAPH_DATA_ACTIONS.DEFAULT_VALUES) || []),
    ];
    const isRefreshing = !!graphLoads.length;

    useEffect(() => {
        if (success?.type) {
            initialFormListStr.current = JSON.stringify(formList);
            setIsDirty(false);
            setTimeout(() => {
                if (success?.data?.data?.id) {
                    navigate(`/progress-report/${id}/client/${clientId}/report/${success?.data?.data?.id}`, {
                        state: { ...info },
                        replace: true,
                    });
                } else {
                    navigate(`/client/${clientId}`, { state: { activeTab: 'Forms' } });
                    // navigate(`/client/${clientId}`, { state: { activeTab: 'Documentation'  } });
                }
            }, 50);

            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    const MAX_RANGE_MONTHS = 13;
    const maxEndDate = dateEditor?.startDate
        ? moment.utc(dateEditor.startDate).add(MAX_RANGE_MONTHS, 'months').format('YYYY-MM-DD')
        : undefined;
    const minStartDate = dateEditor?.endDate
        ? moment.utc(dateEditor.endDate).subtract(MAX_RANGE_MONTHS, 'months').format('YYYY-MM-DD')
        : undefined;
    const isRangeValid =
        !!dateEditor?.startDate &&
        !!dateEditor?.endDate &&
        !moment.utc(dateEditor.endDate).isBefore(moment.utc(dateEditor.startDate)) &&
        !moment.utc(dateEditor.endDate).isAfter(moment.utc(dateEditor.startDate).add(MAX_RANGE_MONTHS, 'months'));

    const changeActiveTab = (tab) => {
        setActiveSection(null);
        setSelectedIndex(null);

        const params = {
            ...info,
        };
        params.activeTab = tab;
        SaveParams(navigate, params);
    };

    const handleChange = (e) => {
        setDateEditor((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
    };

    const handleSaveData = () => {
        if (!isRangeValid) return;
        const params = {
            ...info,
            startDate: dateEditor?.startDate,
            endDate: dateEditor?.endDate,
        };
        setDateEditor((prevState) => ({ ...prevState, showInputs: false }));
        SaveParams(navigate, params);

        if (info?.startDate && info?.endDate && allSelectedGoals?.length) {
            loadGraphData(dateEditor?.startDate, dateEditor?.endDate);
        }
    };

    /** Goal graphs, Target graphs (sessions, baseline, events) and goal default values for the range. */
    const loadGraphData = (startDate, endDate) => {
        const goalIds = allSelectedGoals?.map((g) => g.id);
        // A saved report's own UTC offset, so the graphs date sessions like its PDF (new report: the user's).
        const utcOffset = progressReportById?.utcOffset;
        dispatch(goalActions.getGoalClinicalData({ startDate, endDate, goalIds, utcOffset }));
        dispatch(goalActions.getGoalTargetClinicalData({ startDate, endDate, goalIds, utcOffset }, clientId));
        dispatch(goalActions.getGoalClinicalDefaultValues({ ids: goalIds }));
    };

    // Saved report only: reload its graph data from the latest clinical data. Nothing is saved — the
    // user reviews the refreshed report and saves it or leaves it.
    const reportStartDate = info?.startDate || progressReportById?.startDate;
    const reportEndDate = info?.endDate || progressReportById?.endDate;
    const canRefresh = !!reportId && !!allSelectedGoals?.length && !!reportStartDate && !!reportEndDate;
    const handleRefresh = () => {
        open(
            <CustomDelete
                info="Refresh Graph Data"
                text="Reload the Goal graphs, Target graphs and other goal data of this report from the latest clinical data? Nothing is saved until you click Save."
                innerText={'Refresh'}
                color={'blue'}
                handleDel={() => {
                    loadGraphData(reportStartDate, reportEndDate);
                    close();
                }}
            />
        );
    };

    return (
        <div className="form-detail-header-box">
            <div className="flex-align-center" style={{ gap: 16, width: '33%' }}>
                <button
                    className="form-back-btn-box"
                    onClick={() => {
                        const targetPath = `/client/${clientId}`;
                        if (isDirty) {
                            open(
                                <CustomDelete
                                    info="Unsaved Changes"
                                    text="You have unsaved changes. Are you sure you want to leave this page?"
                                    innerText="Discard Changes"
                                    color="blue"
                                    handleDel={() => {
                                        close();
                                        setIsDirty(false);
                                        setTimeout(() => {
                                            navigate(targetPath, {
                                                state: {
                                                    activeTab: 'Forms',
                                                },
                                            });
                                        }, 50);
                                    }}
                                    handleClose={() => close()}
                                    noClose={true}
                                />
                            );
                        } else {
                            navigate(targetPath, {
                                state: {
                                    activeTab: 'Forms',
                                },
                            });
                        }
                    }}
                >
                    <img src={Images.backBtn} alt="back-btn" />
                    <p>Back to Client</p>
                </button>
                <div className="gray-line" />

                <div className="flex-align-start full-width" style={{ gap: 8 }}>
                    <div style={{ marginTop: 2 }}>
                        <ToolInfo
                            text={
                                'This title helps identify the form within the system. It won’t be visible to users filling out the form.'
                            }
                        />
                    </div>
                    <div className="form-heading-info-box">
                        <div className="form-date-range-info-box">
                            <div className="report-info-title">
                                <TextRow name={info?.reportTitle} fontWeight={600} />
                                {/*{info?.reportTitle}*/}
                            </div>

                            {dateEditor?.showInputs ? (
                                <div className="flex-align-center" style={{ gap: 6 }}>
                                    <DateTypeSelect
                                        noRemoveBtn={true}
                                        name={'startDate'}
                                        handleGetDates={handleChange}
                                        values={dateEditor}
                                        noError={true}
                                        max={dateEditor?.endDate}
                                        min={minStartDate}
                                        width={'fit-content'}
                                    />
                                    <p style={{ margin: 0 }}>-</p>
                                    <DateTypeSelect
                                        noRemoveBtn={true}
                                        name={'endDate'}
                                        handleGetDates={handleChange}
                                        values={dateEditor}
                                        noError={true}
                                        min={dateEditor?.startDate}
                                        max={maxEndDate}
                                        width={'fit-content'}
                                    />
                                    <div className="flex-align-center" style={{ gap: 8, marginLeft: 4 }}>
                                        <button
                                            onClick={handleSaveData}
                                            disabled={!isRangeValid}
                                            style={{ opacity: isRangeValid ? 1 : 0.4 }}
                                            className="reset-btn-by-default"
                                        >
                                            <img src={Images.greenMark} alt="done" />
                                        </button>
                                        <button
                                            className="reset-btn-by-default"
                                            onClick={() =>
                                                setDateEditor({
                                                    showInputs: false,
                                                    startDate: info?.startDate,
                                                    endDate: info?.endDate,
                                                })
                                            }
                                        >
                                            <img
                                                style={{
                                                    filter: 'brightness(0) saturate(100%) invert(44%) sepia(10%) saturate(801%) hue-rotate(183deg) brightness(96%) contrast(91%)',
                                                }}
                                                src={Images.remove}
                                                alt="cancel"
                                            />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-align-center" style={{ gap: 4 }}>
                                    <p className="form-date-range-info-title">{`${renderDate(info?.startDate)} - ${renderDate(
                                        info?.endDate
                                    )}`}</p>
                                    <button
                                        className="reset-btn-by-default"
                                        onClick={() =>
                                            setDateEditor({
                                                showInputs: true,
                                                startDate: info?.startDate,
                                                endDate: info?.endDate,
                                            })
                                        }
                                    >
                                        <img src={Images.editPenUnderline} alt="edit" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
                <div className="form-view-actions-wrapper">
                    <button
                        style={{ width: 'auto' }}
                        onClick={() => changeActiveTab(formTabEnums.CREATOR)}
                        className={activeTab === formTabEnums.CREATOR ? 'form-view-active-btn' : 'form-view-passive-btn'}
                    >
                        <img src={Images.editPen} alt="builder" />
                        <p>Creator</p>
                    </button>
                    {/*<button*/}
                    {/*    style={{width: 'auto'}}*/}
                    {/*    onClick={() => changeActiveTab(formTabEnums.BUILDER)}*/}
                    {/*    className={activeTab === formTabEnums.BUILDER ? 'form-view-active-btn' : 'form-view-passive-btn'}*/}
                    {/*>*/}
                    {/*    <img src={Images.builder} alt="builder" />*/}
                    {/*    <p>Builder</p>*/}
                    {/*</button>*/}
                    {/*<button*/}
                    {/*    onClick={() => changeActiveTab(formTabEnums.PREVIEW)}*/}
                    {/*    className={activeTab === formTabEnums.PREVIEW ? 'form-view-active-btn' : 'form-view-passive-btn'}*/}
                    {/*>*/}
                    {/*    <img src={Images.formPreview} alt="icon" />*/}
                    {/*    <p>Preview</p>*/}
                    {/*</button>*/}
                </div>
            </div>

            <div className="flex-align-center" style={{ gap: 16, width: '33%', justifyContent: 'flex-end' }}>
                {canRefresh && (
                    <SimpleTooltip title={<p>Refresh graph data</p>} placement="top">
                        <button
                            type="button"
                            className="reset-btn-by-default"
                            disabled={isRefreshing}
                            onClick={handleRefresh}
                            style={{ display: 'flex', alignItems: 'center', opacity: isRefreshing ? 0.6 : 1 }}
                        >
                            {isRefreshing ? (
                                <MinLoader margin={'0'} color="#0C66E4" position={'relative'} />
                            ) : (
                                <img src={Images.refresh} alt="refresh" style={{ width: 20, height: 20 }} />
                            )}
                        </button>
                    </SimpleTooltip>
                )}
                <a
                    style={{ textDecoration: 'underline' }}
                    className="learn-how"
                    href={'https://help.therapylake.com/forms/creating-a-new-form'}
                    target={'_blank'}
                    rel="noopener noreferrer"
                >
                    Need help?
                </a>
                <DataFields />
                {(!info?.activeTab || info?.activeTab === formTabEnums.CREATOR) && (
                    <>
                        {canSaveDraft && (
                            <button
                                disabled={isSaving}
                                onClick={() => {
                                    setSavingDraft(true);
                                    onSave(true);
                                }}
                                className="save-as-draft-btn progress-report-draft-btn"
                                style={{ opacity: isSaving ? 0.7 : 1, width: 100 }}
                            >
                                {isSaving && savingDraft ? <MinLoader margin={'0'} color="#0B5DCF" position={'relative'} /> : 'Save Draft'}
                            </button>
                        )}
                        <button
                            disabled={isSaving}
                            onClick={() => {
                                setSavingDraft(false);
                                onSave(false);
                            }}
                            className="form-publish-btn"
                            style={{ opacity: isSaving ? 0.7 : 1, width: 'auto', minWidth: 80 }}
                        >
                            {isSaving && !savingDraft ? (
                                <MinLoader margin={'0'} color="white" position={'relative'} />
                            ) : canSaveDraft ? (
                                'Submit'
                            ) : (
                                'Save'
                            )}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
